/**
 * Favorites Menu System - Utility Functions
 *
 * Provides utility functions for managing favorites including:
 * - localStorage persistence (load, save)
 * - Storage key generation
 * - Data validation
 * - Sorting and filtering
 */

import type { FavoriteMenuItem } from '@/types/favorites';

/**
 * Maximum number of favorites allowed per user
 */
export const MAX_FAVORITES = 100;

/**
 * Generates localStorage key for user-specific favorites
 *
 * @param {string} userId - User identifier
 * @returns {string} Storage key in format "favorites:menu:{userId}"
 *
 * @example
 * const key = generateStorageKey("user-123");
 * // Returns: "favorites:menu:user-123"
 */
export function generateStorageKey(userId: string): string {
  return `favorites:menu:${userId}`;
}

/**
 * Loads favorites from localStorage with error handling
 *
 * @param {string} userId - User identifier
 * @returns {FavoriteMenuItem[]} Array of favorited items, empty array on error
 *
 * @example
 * const favorites = loadFavoritesFromStorage("user-123");
 * // Returns: [{ id: "1", label: "Dashboard", icon: "Home", path: "/", timestamp: 1234567890 }]
 */
export function loadFavoritesFromStorage(userId: string): FavoriteMenuItem[] {
  try {
    const key = generateStorageKey(userId);
    const stored = localStorage.getItem(key);

    if (!stored) {
      return [];
    }

    const parsed = JSON.parse(stored);

    // Validate parsed data is an array
    if (!Array.isArray(parsed)) {
      console.warn('[Favorites] Invalid data format in localStorage, expected array');
      return [];
    }

    // Validate each item in the array
    const validated = parsed.filter((item) => validateFavoriteItem(item));

    // Sort by timestamp descending (most recent first)
    return sortFavoritesByTimestamp(validated);
  } catch (error) {
    console.error('[Favorites] Error loading from storage:', error);
    return [];
  }
}

/**
 * Saves favorites to localStorage with error handling
 *
 * @param {string} userId - User identifier
 * @param {FavoriteMenuItem[]} items - Array of favorite items to save
 * @returns {boolean} True if save successful, false otherwise
 *
 * @example
 * const success = saveFavoritesToStorage("user-123", [
 *   { id: "1", label: "Dashboard", icon: "Home", path: "/", timestamp: Date.now() }
 * ]);
 */
export function saveFavoritesToStorage(
  userId: string,
  items: FavoriteMenuItem[]
): boolean {
  try {
    const key = generateStorageKey(userId);
    const serialized = JSON.stringify(items);
    localStorage.setItem(key, serialized);
    return true;
  } catch (error) {
    console.error('[Favorites] Error saving to storage:', error);
    return false;
  }
}

/**
 * Validates a favorite item has all required fields with correct types
 *
 * @param {any} item - Item to validate
 * @returns {boolean} True if valid FavoriteMenuItem, false otherwise
 *
 * @example
 * const isValid = validateFavoriteItem({
 *   id: "1",
 *   label: "Dashboard",
 *   icon: "Home",
 *   path: "/",
 *   timestamp: 1234567890
 * });
 * // Returns: true
 */
export function validateFavoriteItem(item: any): item is FavoriteMenuItem {
  if (!item || typeof item !== 'object') {
    return false;
  }

  // Check required fields exist and have correct types
  const hasRequiredFields =
    typeof item.id === 'string' &&
    typeof item.label === 'string' &&
    typeof item.icon === 'string' &&
    typeof item.path === 'string' &&
    typeof item.timestamp === 'number';

  if (!hasRequiredFields) {
    return false;
  }

  // Validate optional fields if present
  if (item.category !== undefined && typeof item.category !== 'string') {
    return false;
  }

  if (
    item.metadata !== undefined &&
    (typeof item.metadata !== 'object' || Array.isArray(item.metadata))
  ) {
    return false;
  }

  return true;
}

/**
 * Sorts favorites by timestamp in descending order (most recent first)
 *
 * @param {FavoriteMenuItem[]} items - Array of favorite items to sort
 * @returns {FavoriteMenuItem[]} Sorted array (new array, does not mutate input)
 *
 * @example
 * const sorted = sortFavoritesByTimestamp([
 *   { id: "1", label: "Old", icon: "Home", path: "/", timestamp: 1000 },
 *   { id: "2", label: "New", icon: "Star", path: "/new", timestamp: 2000 }
 * ]);
 * // Returns: [{ id: "2", ... }, { id: "1", ... }]
 */
export function sortFavoritesByTimestamp(
  items: FavoriteMenuItem[]
): FavoriteMenuItem[] {
  return [...items].sort((a, b) => b.timestamp - a.timestamp);
}

/**
 * Checks if more favorites can be added based on MAX_FAVORITES limit
 *
 * @param {number} currentCount - Current number of favorites
 * @returns {boolean} True if can add more favorites, false if at limit
 *
 * @example
 * const canAdd = canAddMoreFavorites(5);
 * // Returns: true (5 < 100)
 *
 * const cannotAdd = canAddMoreFavorites(100);
 * // Returns: false (100 >= 100)
 */
export function canAddMoreFavorites(currentCount: number): boolean {
  return currentCount < MAX_FAVORITES;
}
